import cn from 'classnames';

import NO_IMAGE from 'assets/images/slide-out/user-avatar.svg';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import isNotNilValue from 'utils/predicates/any/isNotNilValue.util';
import isFalsy from 'utils/predicates/isFalsy.util';

import CN from './ClientProfilePicture.module.scss';

const PictureContent = ({ isImageLoading, imageSrc, initials, profileImageAlt }) => {
  if (fromAnyToBool(isImageLoading)) {
    return (
      <div className={CN.loadingSpinner}>
        <InlineSpinner variant="big" />
      </div>
    );
  }

  if (fromAnyToBool(imageSrc)) {
    return <img src={imageSrc} alt={profileImageAlt} className={cn({ [CN.defaultPlaceholderImage]: imageSrc === NO_IMAGE })} />;
  }

  return (
    <div className={CN.initials}>
      <span>{fromAnyToString(initials)}</span>
    </div>
  );
};

export const ClientProfilePicture = ({ imageSrc, initials, onEditClick = alwaysNoop, isLoading = false, isProcessing = false, imageError = null, isEditDisabled = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const isImageLoading = isLoading || isProcessing;
  const displayErrorMessage = fromAnyToTrimmed(imageError?.message) || t('composite.clientProfile.details.failedLoadingImage');
  const profileImageAlt = t('scheduler.header.profileImage');

  if (isNotNilValue(imageError) && isFalsy(fromAnyToBool(initials))) {
    return (
      <div className={CN.imageError}>
        <span>{displayErrorMessage}</span>
      </div>
    );
  }
  const isDefaultPlaceholderAvatar = !isImageLoading && imageSrc === NO_IMAGE;

  return (
    <div data-bem="ClientProfilePicture" className={cn({ [CN.profilePictureContainer]: true, [CN.dark]: isDarkMode })}>
      <div className={CN.profilePictureWrapper}>
        <div
          className={cn({
            [CN.profilePicture]: true,
            [CN.isLoading]: isImageLoading,
            [CN.placeholderFrame]: isDefaultPlaceholderAvatar,
          })}
        >
          <PictureContent isImageLoading={isImageLoading} imageSrc={imageSrc} initials={initials} profileImageAlt={profileImageAlt} />
        </div>

        {!isEditDisabled && (
          <div className={CN.editPhotoButton}>
            <Button variant="circlePrimary" aria-label="Edit photo" width="auto" noMargin noVerticalMargin onClick={onEditClick} disabled={isImageLoading}>
              <Icon variant="pencilWhiteSmall" isCentered width="sm" />
            </Button>
          </div>
        )}
      </div>

      {isNotNilValue(imageError) && (
        <div className={CN.imageError}>
          <span>{displayErrorMessage}</span>
        </div>
      )}
    </div>
  );
};
